import { apiClient, toUnixSeconds } from '@/shared/api';
import type { AuthTokens } from '@/shared/api';

import type {
  AuthTokensResponse,
  ForgotPasswordResponse,
  LoginResponse,
  ProfileResponse,
  RegisterResponse
} from './types';

/**
 * Every auth endpoint, in one place.
 *
 * Notes that matter:
 *
 *  - `skipAuth: true` on the unauthenticated calls. Without it the client would
 *    attach whatever stale access token happens to be in memory, and a 401 from
 *    /login would trigger a pointless refresh round-trip.
 *  - Nothing here logs. Codes, tokens and secrets pass through this module.
 */

const BASE = '/api/v1/auth';
const ACCOUNT = '/api/v1/account';

export const authApi = {
  register: (body: { email: string; password: string; language: string }) =>
    apiClient.post<RegisterResponse>(`${BASE}/register`, body, { skipAuth: true }),

  confirmEmail: (body: { token: string }) =>
    apiClient.post<null>(`${BASE}/confirm-email`, body, { skipAuth: true }),

  login: (body: { email: string; password: string }) =>
    apiClient.post<LoginResponse>(`${BASE}/login`, body, { skipAuth: true }),

  /**
   * Ends the session server-side.
   *
   * Required now that the refresh token lives in an httpOnly cookie: the SPA
   * cannot delete that cookie itself, so without this call "sign out" would
   * leave a valid thirty-day credential in the browser. The backend revokes the
   * whole token family and clears the cookie; it always answers success, so this
   * never blocks the local sign-out.
   */
  logout: () => apiClient.post<null>(`${BASE}/logout`, undefined, { skipRefresh: true }),

  forgotPassword: (body: { email: string }) =>
    apiClient.post<ForgotPasswordResponse>(`${BASE}/forgot-password`, body, { skipAuth: true }),

  resetPassword: (body: { token: string; newPassword: string }) =>
    apiClient.post<null>(`${BASE}/reset-password`, body, { skipAuth: true }),

  me: () => apiClient.get<ProfileResponse>(`${ACCOUNT}/me`),

  changeLanguage: (language: string) => apiClient.put<null>(`${ACCOUNT}/language`, { language })
};

/**
 * Converts the wire token payload into what the token store wants.
 *
 * The backend speaks ISO-8601; `tokenStore` reasons in Unix seconds so it can
 * compare against `Date.now() / 1000` without re-parsing a string on every
 * request. One conversion, here, at the boundary.
 */
export function toAuthTokens(tokens: AuthTokensResponse): AuthTokens {
  return {
    accessToken: tokens.accessToken,
    refreshToken: tokens.refreshToken,
    expiresAt: toUnixSeconds(tokens.accessTokenExpiresAt)
  };
}

/** TanStack Query keys for the authenticated account surface. */
export const accountKeys = {
  all: ['account'] as const,
  me: () => ['account', 'me'] as const
};
